import * as React from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from "redux";
import bindAllOfThis from "src/utils/BindThisHelper";
import Routes from 'src/core/Routes';
import { MissionState } from "src/redux/CheckpointReducer";
import { UserState } from "src/redux/UserReducer";
import { ConfigState } from "src/redux/ConfigReducer";
import { ReduxStoreModel } from "src/redux/ReduxModel";
import { PageProps } from "src/models/common/ComponentProps";
import SceneManager from "src/constants/SceneManager";
import CheckpointAction, { KnownCheckpointStates } from "src/redux/CheckpointActions";
import { Checkpoints, Checkpoint } from "src/constants/Checkpoints";
import SimpleCheckpointStatusDTO from "src/models/frontend/redux/SimpleCheckpointStatusDTO";
import { Grades } from "src/constants/Grades";
import { Planets } from "src/constants/Planets";
import { Games } from "src/constants/Games";
import AudioPlayer from "src/components/common/AudioPlayer";
import 'src/pages/student/StudentPages.less';
import { Track } from 'src/providers/TelemetryService';

interface SpaceStationHomeProps extends PageProps<{}> {
    // From Redux
    Mission: MissionState;
    User: UserState;
    Config: ConfigState;
    updateMissionStatus: (request: SimpleCheckpointStatusDTO) => void;
    updateFreshness: (request: KnownCheckpointStates) => void;
}

interface SpaceStationHomeState {
    audioUrl: string;
}

class SpaceStationHome extends React.Component<SpaceStationHomeProps, SpaceStationHomeState> {
    constructor(props: SpaceStationHomeProps) {
        super(props);
        bindAllOfThis(this, SpaceStationHome.prototype);

        this.state = {
            audioUrl: ""
        };
    }

    onNextClick(): void {
        // Increment the step and re-render!
        let { checkpointId, stepId, planetId } = this.props.Mission.current; // #TypescriptMagic
        let nextStep = SceneManager.GetNextStep(checkpointId, stepId, planetId);
        let newMissionStatus: SimpleCheckpointStatusDTO = {
            planetId: nextStep.planetId,
            checkpointId: nextStep.checkpointId,
            stepId: nextStep.stepId,
            currentDirection: "forwards",
        };
        this.props.updateMissionStatus(newMissionStatus);
        return;
    }

    skipToCheckpoint(checkpoint: Checkpoint): void {
        this.props.updateMissionStatus({
            planetId: this.props.Mission.current.planetId,
            checkpointId: checkpoint.id,
            stepId: 1,
            currentDirection: "forwards",
        });
    }

    onBackClick(): void {
        // Increment the step and re-render!
        let { checkpointId, stepId, planetId } = this.props.Mission.current; // #TypescriptMagic
        let nextStep = SceneManager.GetPreviousStep(checkpointId, stepId, planetId);
        let newMissionStatus: SimpleCheckpointStatusDTO = {
            planetId: nextStep.planetId,
            checkpointId: nextStep.checkpointId,
            stepId: nextStep.stepId,
            currentDirection: "backwards",
        };
        this.props.updateMissionStatus(newMissionStatus);
        return;
    }

    onChangeAudio(audioUrl: string) {
        if (audioUrl !== this.state.audioUrl) {
            this.setState({ audioUrl: audioUrl });
        }
    }

    componentDidMount(): void {
        const { checkpointId, stepId } = this.props.Mission.current;
        const checkpoint = Checkpoints.FindById(checkpointId);
        Track.SpaceStation(checkpoint.name, checkpointId, stepId);
    }

    componentDidUpdate(prevProps, prevState): void {
        const { checkpointId: currentCheckpointId, stepId: currentStepId } = this.props.Mission.current;
        const { checkpointId: prevCheckpointId, stepId: prevStepId } = prevProps.Mission.current;

        if(prevCheckpointId !== currentCheckpointId || prevStepId !== currentStepId) {
            const currentCheckpoint = Checkpoints.FindById(currentCheckpointId);
            Track.SpaceStation(currentCheckpoint.name, currentCheckpoint.id, currentStepId);
        }
    }

    render() {
        let currentGrade = Grades.FindById(this.props.User.gradeId);
        let currentPlanet = Planets.FindById(this.props.Mission.current.planetId);
        let currentGame = Games.FindByGradeAndPlanet(currentGrade.id, currentPlanet.id);
        let checkpoint = Checkpoints.FindById(this.props.Mission.current.checkpointId);
        let level = SceneManager.FindByIds(this.props.Mission.current.checkpointId, this.props.Mission.current.stepId);

        if (!this.props.Mission.isReplayModeActive && this.props.Config.previousLocation != null) {
            return <Redirect to={Routes.LOGIN}/>;
        }

        if (this.props.Mission.state === "fresh") {
            // User has logged in recently and needs to be welcomed back
            if (this.props.Mission.current.checkpointId > Checkpoints.Onboarding.id) {
                return <Redirect to={Routes.STUDENT_RETURN} />;
            } else {
                this.props.updateFreshness("not-fresh");
            }
        }

        if (checkpoint.type === "Planet") {
            return <Redirect to={Routes.STUDENT_PLANET} />;
        } else if (checkpoint.type === "SpaceStation") {
            if (level == null || level.component == null) {
                // Catches errors with the sceneManager. Move on to next area essentially
                if (this.props.Mission.state !== "none") {
                    this.onNextClick();
                }
                return null;
            }
            return <div>
                <AudioPlayer
                    audioURL={this.state.audioUrl}
                    isPlaying={true} />
                <level.component
                    onNext={this.onNextClick}
                    onBack={this.onBackClick}
                    currentDirection={this.props.Mission.currentDirection}
                    currentGrade={currentGrade}
                    currentPlanet={currentPlanet}
                    currentGame={currentGame}
                    Mission={this.props.Mission}
                    User={this.props.User}
                    Config={this.props.Config}
                    onChangeAudio={this.onChangeAudio}
                    skipToCheckpoint={this.skipToCheckpoint}
                />
            </div>;
        }

        return null;
    }
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return {
        updateMissionStatus: (request: SimpleCheckpointStatusDTO) => CheckpointAction.UpdateCurrentUserStatus(dispatch, request),
        updateFreshness: (request: KnownCheckpointStates) => CheckpointAction.SetFreshnessStatus(dispatch, request)
    };
}

function mapStateToProps(state: ReduxStoreModel) {
    return {
        Mission: state.Mission,
        User: state.User,
        Config: state.Config,
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(SpaceStationHome);



// WEBPACK FOOTER //
// ./src/pages/student/SpaceStationHome.tsx